import { useEffect, useState } from 'react';
import { adminApi, formatBytes, formatDate, formatMoney } from '../api';
import { MonitoringSection } from './AdminMonitoring';
import { FleetSection } from './AdminFleet';
import { AlertsSection } from './AdminAlerts';
import { FinanceSection } from './AdminFinance';
import { SupportSection } from './AdminSupport';
import { IncidentsSection } from './AdminIncidents';

interface Overview {
  clusters: {
    id: string;
    region: string;
    state: string;
    databaseCount: number;
    storageBytes: number;
    activeSessions: number;
    healthStatus: string;
    healthCheckedAt: string | null;
    lastUsageBatch: { receivedAt: string; samplesCount: number } | null;
  }[];
  databases: { total: number; active: number; paymentRequired: number; failed: number; storageBytes: number };
  accounts: { total: number; paymentRequired: number; disabled: number };
}

function useAdmin<T>(path: string, dep: unknown = null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi<T>(path)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [path, dep]);
  return { data, error };
}

export function AdminPage() {
  const [tick, setTick] = useState(0);
  const overview = useAdmin<Overview>('/overview', tick);
  const topUsage = useAdmin<{
    accounts: { accountId: string; name: string; state: string; totalMicro: number }[];
  }>('/top/usage', tick);
  const topStorage = useAdmin<{
    accounts: { accountId: string; name: string; state: string; storageBytes: number; databases: number }[];
  }>('/top/storage', tick);
  const failed = useAdmin<{
    jobs: { id: string; databaseId: string; accountId: string; error: string | null; createdAt: string }[];
  }>('/provisioning/failed', tick);
  const [accountId, setAccountId] = useState('');
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [adj, setAdj] = useState({ amount: 0, bucket: 'paid', reason: '' });
  const [msg, setMsg] = useState<string | null>(null);
  if (overview.error) return <p className="error">{overview.error}</p>;
  const o = overview.data;
  return (
    <div>
      <h1>Operations</h1>
      <AlertsSection tick={tick} />
      <SupportSection tick={tick} />
      <IncidentsSection tick={tick} />
      <MonitoringSection tick={tick} />
      <FleetSection tick={tick} />
      <FinanceSection tick={tick} />
      <div className="grid cols-3">
        <div className="card">
          <div className="stat">Databases</div>
          <div className="stat-value">{o?.databases.total ?? '…'}</div>
          <div className="muted">
            {o?.databases.active} available · {o?.databases.paymentRequired} paused · {o?.databases.failed}{' '}
            failed · {formatBytes(o?.databases.storageBytes ?? 0)}
          </div>
        </div>
        <div className="card">
          <div className="stat">Accounts</div>
          <div className="stat-value">{o?.accounts.total ?? '…'}</div>
          <div className="muted">
            {o?.accounts.paymentRequired} payment required · {o?.accounts.disabled} disabled
          </div>
        </div>
        <div className="card">
          <div className="stat">Clusters</div>
          {o?.clusters.map((c) => (
            <div key={c.id}>
              <strong>{c.id}</strong> {c.region} · {c.state} · {c.healthStatus} · {c.databaseCount} dbs ·{' '}
              {c.activeSessions} sessions
              <div className="muted">
                last usage batch:{' '}
                {c.lastUsageBatch
                  ? `${formatDate(c.lastUsageBatch.receivedAt)} (${c.lastUsageBatch.samplesCount} samples)`
                  : 'none'}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid cols-2">
        <div className="card">
          <h2>Top accounts by usage (30d)</h2>
          <table>
            <tbody>
              {topUsage.data?.accounts.map((a) => (
                <tr key={a.accountId}>
                  <td>
                    <a href="#" onClick={(e) => (e.preventDefault(), setAccountId(a.accountId))}>
                      {a.name}
                    </a>{' '}
                    <span className="muted">{a.state}</span>
                  </td>
                  <td>{formatMoney(a.totalMicro, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card">
          <h2>Top accounts by storage</h2>
          <table>
            <tbody>
              {topStorage.data?.accounts.map((a) => (
                <tr key={a.accountId}>
                  <td>
                    <a href="#" onClick={(e) => (e.preventDefault(), setAccountId(a.accountId))}>
                      {a.name}
                    </a>{' '}
                    <span className="muted">{a.databases} dbs</span>
                  </td>
                  <td>{formatBytes(a.storageBytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card">
        <h2>Failed provisioning jobs</h2>
        {failed.data?.jobs.length === 0 && <p className="muted">None.</p>}
        <table>
          <tbody>
            {failed.data?.jobs.map((j) => (
              <tr key={j.id}>
                <td>{formatDate(j.createdAt)}</td>
                <td>{j.databaseId}</td>
                <td>
                  <a href="#" onClick={(e) => (e.preventDefault(), setAccountId(j.accountId))}>
                    {j.accountId}
                  </a>
                </td>
                <td className="error">{j.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h2>Account</h2>
        <div className="row">
          <input
            placeholder="acct_…"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          <button
            className="secondary"
            onClick={() =>
              adminApi<Record<string, unknown>>(`/accounts/${accountId}`)
                .then(setDetail)
                .catch((e: Error) => setMsg(e.message))
            }
          >
            Inspect
          </button>
          <button
            className="danger"
            onClick={async () => {
              const reason = prompt('Reason for disabling this account?');
              if (!reason) return;
              await adminApi(`/accounts/${accountId}/disable`, { method: 'POST', json: { reason } });
              setTick((t) => t + 1);
              setMsg('Account disabled');
            }}
          >
            Disable
          </button>
          <button
            className="secondary"
            onClick={async () => {
              await adminApi(`/accounts/${accountId}/enable`, { method: 'POST' });
              setTick((t) => t + 1);
              setMsg('Account enabled');
            }}
          >
            Enable
          </button>
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <input
            type="number"
            placeholder="amount in USD (negative to debit)"
            value={adj.amount}
            onChange={(e) => setAdj({ ...adj, amount: Number(e.target.value) })}
            style={{ maxWidth: 200 }}
          />
          <select
            value={adj.bucket}
            onChange={(e) => setAdj({ ...adj, bucket: e.target.value })}
            style={{ maxWidth: 160 }}
          >
            <option value="paid">paid</option>
            <option value="promotional">promotional</option>
          </select>
          <input
            placeholder="reason"
            value={adj.reason}
            onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
          />
          <button
            onClick={async () => {
              await adminApi(`/accounts/${accountId}/wallet-adjustments`, {
                method: 'POST',
                json: {
                  amountMicroUsd: Math.round(adj.amount * 1_000_000),
                  bucket: adj.bucket,
                  reason: adj.reason,
                },
              });
              setMsg('Adjustment recorded');
              setTick((t) => t + 1);
            }}
          >
            Adjust wallet
          </button>
        </div>
        {msg && <p className="ok">{msg}</p>}
        {detail && <pre>{JSON.stringify(detail, null, 2)}</pre>}
      </div>
    </div>
  );
}
